/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { createContext } from 'use-context-selector';
import { useAuth } from '@auth-wrapper';
import {
  getEnergyPricesAndMargins as getEnergyPricesAndMarginsAPI,
  type GetEnergyPricesParams,
  ModalityTypeEnum,
  ProductTypeEnum,
  EnergyTypeEnum,
  RiskTypeEnum,
} from '@services/scenarios-simulator';
import { normalizeProjected } from '@utils/scenarios-simulator/normalize-projections';
import { notifyError, notifyWarning } from '@components/atoms/toast/services';
import { IMarginFee, IRawEnergyPriceProjectionComponent } from '@schemas/scenarios-simulator/types';

export { ModalityTypeEnum, ProductTypeEnum, EnergyTypeEnum, RiskTypeEnum };
export type { GetEnergyPricesParams as IGetEnergyPricesAndMarginsParms };

interface EnergyPricesContextData {
  projections: Record<
    string,
    {
      rawEnergyPrice: IRawEnergyPriceProjectionComponent;
      marginFee: IMarginFee;
      isLoading: boolean;
      error?: any;
      hasExpiredPrices?: boolean;
      hasIncompletePrices?: boolean;
    }
  >;
  getEnergyPricesAndMargins: (
    unitId: string,
    params: GetEnergyPricesParams,
    options?: EnergyPriceFetchOptions,
  ) => Promise<void>;
  clearExpiredPrices: (unitId: string) => void;
  clearProjection: (unitId: string) => void;
  isLoadingEnergyPricesAndMargins: boolean;
  isInvalidPrices: boolean;
}

const NO_VALID_PRICES_ERROR_CODES = new Set(['IPE-001', 'IPE-002', 'PPE-004', 'PPE-002', 'PPE-001', 'PPE-005']);

const INCOMPLETE_PRICES_ERROR_CODES = new Set(['PPE-006', 'IPE-005']);

export interface EnergyPriceFetchOptions {
  notifyMissingParams?: boolean;
}

const REQUIRED_PARAM_LABELS: Partial<Record<keyof GetEnergyPricesParams, string>> = {
  product_type: 'Produto',
  supply_start_date: 'Início do fornecimento',
  supply_final_date: 'Fim do fornecimento',
  energy_type: 'Tipo de energia',
  state: 'Estado',
  modality_type: 'Modalidade de contratação',
  risk_type: 'Perfil de risco',
};

export const EnergyPricesContext = createContext({} as EnergyPricesContextData);

interface Provider {
  children: React.ReactNode;
}

const EnergyPricesProvider: React.FC<Provider> = ({ children }) => {
  const {
    authStatus: { accessToken: token },
  } = useAuth();

  const [projections, setProjections] = React.useState<
    Record<
      string,
      {
        rawEnergyPrice: IRawEnergyPriceProjectionComponent;
        marginFee: IMarginFee;
        isLoading: boolean;
        error?: any;
        hasExpiredPrices?: boolean;
        hasIncompletePrices?: boolean;
      }
    >
  >({});
  const [isLoadingEnergyPricesAndMargins] = React.useState<boolean>(false);
  const clearExpiredPrices = React.useCallback((unitId: string) => {
    setProjections((prev) => ({
      ...prev,
      [unitId]: { ...prev[unitId], hasExpiredPrices: false },
    }));
  }, []);
  const clearProjection = React.useCallback((unitId: string) => {
    setProjections((prev) => {
      const newProjections = { ...prev };
      delete newProjections[unitId];
      return newProjections;
    });
  }, []);

  const isInvalidPrices = React.useMemo(
    () => Object.values(projections).some((p) => p.hasExpiredPrices || p.hasIncompletePrices),
    [projections],
  );
  const getEnergyPricesAndMargins = React.useCallback(
    async (unitId: string, params: GetEnergyPricesParams, options: EnergyPriceFetchOptions = {}): Promise<void> => {
      if (!params || !unitId) return;

      if (!token) {
        notifyError('No authentication token available');
        return;
      }

      const baseRequiredParams: (keyof GetEnergyPricesParams)[] = [
        'product_type',
        'supply_start_date',
        'supply_final_date',
        'energy_type',
        'state',
      ];
      const isClarkeAclRetailer = params.product_type !== ProductTypeEnum.ACL_WHOLESALER;
      const requiredParams = isClarkeAclRetailer
        ? baseRequiredParams
        : [...baseRequiredParams, 'modality_type', 'risk_type'];
      const missingRequiredParams = requiredParams.filter((key) => {
        const value = params[key as keyof GetEnergyPricesParams];
        return value === null || value === undefined || value === '';
      });

      if (missingRequiredParams.length > 0) {
        if (!options.notifyMissingParams) return;
        const missingLabels = missingRequiredParams.map(
          (key) => REQUIRED_PARAM_LABELS[key as keyof GetEnergyPricesParams] ?? key,
        );
        notifyWarning(`Preencha ${missingLabels.join(', ')} para buscar os preços de energia e margens.`);
        return;
      }

      setProjections((prev) => ({
        ...prev,
        [unitId]: {
          ...prev[unitId],
          isLoading: true,
          error: undefined,
          hasExpiredPrices: false,
          hasIncompletePrices: false,
        },
      }));
      try {
        const result = await getEnergyPricesAndMarginsAPI(params, token);
        const rawPriceList = result.rawEnergyPrice?.rawPriceList;
        const hasIncompletePrices = !rawPriceList || Object.keys(rawPriceList).length === 0;
        setProjections((prev) => ({
          ...prev,
          [unitId]: {
            rawEnergyPrice: result.rawEnergyPrice,
            marginFee: result.marginFee,
            isLoading: false,
            hasExpiredPrices: false,
            hasIncompletePrices,
          },
        }));

        if (hasIncompletePrices) {
          notifyWarning('Preços recebidos estão incompletos. Preencha os preços manualmente para continuar.');
        }
      } catch (error) {
        const errorCode = (
          error as { response?: { data?: { error_code?: string; rawEnergyPrice?: any; marginFee?: any } } }
        ).response?.data?.error_code;
        const hasExpiredPrices = errorCode && NO_VALID_PRICES_ERROR_CODES.has(errorCode);
        const hasIncompletePrices = Boolean(errorCode && INCOMPLETE_PRICES_ERROR_CODES.has(errorCode));
        const errorData = (error as { response?: { data?: any } }).response?.data;
        const normalizeRawFromError = (ed: any) => {
          if (!ed) return null;
          if (ed.rawEnergyPrice) return ed.rawEnergyPrice;
          const dataBlock = ed.data ?? ed;
          const rawList = dataBlock?.raw_energy_price_list ?? dataBlock?.rawEnergyPrice ?? null;
          if (!rawList) return null;
          return {
            rawPriceList: normalizeProjected(rawList),
            origin: dataBlock?.origin ?? ed?.origin ?? null,
            energyType: dataBlock?.energy_type ?? ed?.energyType ?? null,
            submarket: dataBlock?.submarket ?? ed?.submarket ?? null,
          } as IRawEnergyPriceProjectionComponent;
        };

        const normalizeMarginFromError = (ed: any) => {
          if (!ed) return null;
          if (ed.marginFee) return ed.marginFee;
          const dataBlock = ed.data ?? ed;
          const mf = dataBlock?.margin_fee ?? dataBlock?.marginFee ?? null;
          if (!mf) return null;
          return {
            type: 'ABSOLUTE',
            aux__useSamePricePerYears: false,
            values: normalizeProjected(mf) as Record<string, number>,
          } as IMarginFee;
        };

        setProjections((prev) => {
          const rawFromError = normalizeRawFromError(errorData) ?? prev[unitId]?.rawEnergyPrice;
          const marginFromError = normalizeMarginFromError(errorData) ?? prev[unitId]?.marginFee;
          const shouldKeepPricesFromError = hasExpiredPrices || hasIncompletePrices;
          return {
            ...prev,
            [unitId]: {
              rawEnergyPrice: shouldKeepPricesFromError && rawFromError ? rawFromError : prev[unitId]?.rawEnergyPrice,
              marginFee: shouldKeepPricesFromError && marginFromError ? marginFromError : prev[unitId]?.marginFee,
              isLoading: false,
              error,
              hasExpiredPrices,
              hasIncompletePrices,
            },
          } as Record<
            string,
            {
              rawEnergyPrice: IRawEnergyPriceProjectionComponent;
              marginFee: IMarginFee;
              isLoading: boolean;
              error?: any;
              hasExpiredPrices?: boolean;
              hasIncompletePrices?: boolean;
            }
          >;
        });

        if (hasIncompletePrices) {
          notifyWarning(
            'A projeção não cobre todos os anos do fornecimento. Preencha manualmente os anos faltantes para continuar.',
          );
        } else if (hasExpiredPrices) {
          notifyWarning('Projeção encontrada, mas todos os preços estão expirados. A simulação não pode ser gerada.');
        } else if (errorCode && NO_VALID_PRICES_ERROR_CODES.has(errorCode)) {
          notifyWarning('Não há preços válidos registrados para o período selecionado.');
        } else {
          notifyError('Erro ao obter preços de energia e margens');
        }

        if (!hasExpiredPrices && !hasIncompletePrices) throw error;
        return;
      }
    },
    [token],
  );

  return (
    <EnergyPricesContext.Provider
      value={{
        projections,
        getEnergyPricesAndMargins,
        clearExpiredPrices,
        clearProjection,
        isLoadingEnergyPricesAndMargins,
        isInvalidPrices,
      }}
    >
      {children}
    </EnergyPricesContext.Provider>
  );
};

export default EnergyPricesProvider;
